import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationStart, ParamMap, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { APP } from 'src/app/config/app';
import { IBooking, IBookingScheduler, IFavourites, IUser, IUserDriver, IUserRegular } from 'src/app/models';
import { IDocument } from 'src/app/models/documents/document';
import { IDocumentCreate } from 'src/app/models/documents/documentCreate';
import { FavouriteType, NotificationTypes, UserRole, UserSortType, UserStatus, UserType } from 'src/app/models/enums';
import { IDocumentType } from 'src/app/models/enums/documentType';
import { UsersResolver } from 'src/app/resolvers/users.resolver';
import { AuthService, BookingSchedulersService, BookingsService, NotificationService, StorageService, TripsService, UsersService } from 'src/app/services';
import {
  IUsersFilters,
  USER_FILTER_KEYS,
  deserializeUsersFilters,
  hasAnyFilterKey,
  serializeUsersFilters,
} from 'src/app/filters';
import { FavouritesService } from 'src/app/services/favourites/favourites.service';
import { ApiRouterService } from 'src/app/services/http/api.router';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialBookingsSchedulerState } from 'src/app/store/initialisation/bookings-schedulers';
import { initialFavouritesState } from 'src/app/store/initialisation/favourites';
import { initialUserDriverState } from 'src/app/store/initialisation/userDriver';
import { initialUserRegularState } from 'src/app/store/initialisation/userRegular';
import { initialUserState } from 'src/app/store/initialisation/users';
import { IBookingsSchedulersState, IFavouritesState, IPagination } from 'src/app/store/states';
import { images } from 'src/images';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    standalone: false
})
export class UsersComponent implements OnInit, AfterViewInit, OnDestroy {
  public images = images;
  public userSelected: any;
  public newUser: any;
  public users = initialUserState;
  public TooltipPosition = TooltipPosition;
  public towns: any[] = [];
  public userType = UserType;
  public userStatus = UserStatus;
  public adminRole: UserRole;

  public updateDocuments: Modal;
  public updateDocumentsShowed: boolean = false;
  public documents: IDocument[] = [];

  public bookings: IBooking[] = [];
  public viewBookingsUserId: number;
  public viewBookings: Modal;
  public viewBookingsShowed: boolean = false;

  public activePasses: Modal;
  public activePassesShowed: boolean = false;
  public activePassesUserId: number | undefined;
  public activePassesAllowedClientIds: number[] = [];
  private loggedUserClientIds: number[] = [];
  public showDate = true;
  private routerSubscription: Subscription;

  private clientsSelected: number[] = [];
  private townsSelected: number[] = [];
  private operatorsSelected: number[] = [];
  private statusSelected: UserStatus[] = [];
  public searchValue: string = '';
  private language: string = 'en';
  public searching = false;

  private queryParamsSub?: Subscription;
  private routeParamsSub?: Subscription;

  public controller = new AbortController();
  public interval: any;
  public getBookingsInterval: any;

  public changeStatus: Modal;
  public changeStatusShowed: boolean = false;
  public changeStatusSelected: string;

  public blockUser: Modal;
  public blockUserShowed: boolean = false;
  public reason: string;
  public viewFavouritesUsers: Modal;
  public viewFavouritesShowed: boolean = false;
  public viewFavouritesSelected: string;
  public favourites: IFavouritesState = initialFavouritesState;
  public favouriteUserId: number;
  public favouritesPagination: IPagination;
  public recurrents: IBookingsSchedulersState = initialBookingsSchedulerState;
  public recurrentUserId: number;
  public recurrentPagination: IPagination;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    private usersService: UsersService,
    private usersResolver: UsersResolver,
    private activatedRoute: ActivatedRoute,
    public utilsService: UtilsService,
    public routerService: ApiRouterService,
    public storageService: StorageService,
    public authService: AuthService,
    public router: Router,
    public notificationService: NotificationService,
    public tripsService: TripsService,
    public favouritesService: FavouritesService,
    public bookingSchedulersService: BookingSchedulersService,
    public bookingsService: BookingsService,
    public appComponent: AppComponent,
    public cdr: ChangeDetectorRef
  ) {
    renderer.listen('window', 'click', (e: Event) => {
      if (!this.utilsService.isTextSelected()) {
        const modal = document.getElementById('userDetailsContainer')!;
        const componentOpen = document.getElementById('usersContainer')!;
        const modals: Node[] = [];
        modals.push(document.getElementById('viewBookings') as Node);
        modals.push(document.getElementById('updateDocuments') as Node);
        modals.push(document.getElementById('activePasses') as Node);
        modals.push(document.getElementById('changeStatus') as Node);
        modals.push(document.getElementById('blockUser') as Node);
        modals.push(document.getElementById('viewFavouritesUsers') as Node);
        document.getElementById('toast-success') && modals.push(document.getElementById('toast-success') as Node);
        document.getElementById('toast-warning') && modals.push(document.getElementById('toast-warning') as Node);
        document.getElementById('toast-danger') && modals.push(document.getElementById('toast-danger') as Node);
        const close = utilsService.closeCustomModalsWithModals(e, modal, modals, componentOpen);
        if (close && this.userSelected) {
          this.router.navigate([this.listBasePath], {
            queryParams: this.activatedRoute.snapshot.queryParams,
          });
        }
      }
    });
  }

  get listUserType(): UserType {
    // The list type lives in the path (/users vs /drivers); routeConfig is not reliable from a child component
    return this.router.url.split('?')[0].split('/')[1] === 'drivers' ? UserType.Driver : UserType.Regular;
  }

  get listBasePath(): string {
    return this.listUserType === UserType.Driver ? '/drivers' : '/users';
  }

  async ngOnInit() {
    this.language = await this.utilsService.getLanguage();
    this.adminRole = await this.storageService.getRole();
    this.literalService.setTitle('sidebar.users', ' - NEMI Backoffice');

    // Path params drive: user detail view (/users/:id or /users/:type/:id).
    this.routeParamsSub = this.activatedRoute.paramMap.subscribe((params) => {
      const userId = params.get('id');
      if (userId) {
        this.showUserData({ type: this.listUserType, id: Number(userId) });
        if (history.state?.fromFavouriteBooking) {
          this.showFavouriteRoutes(null, Number(userId));
        }
      } else if (this.userSelected) {
        this.userSelected = undefined;
      }
    });

    // Hydration from storage happens in usersHydrationGuard before the resolver
    // fires, so there's exactly one API call per sidebar entry.
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.applyQueryParamsToState(params);
    });

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.viewBookingsShowed) this.viewBookings.toggle();
        if (this.changeStatusShowed) this.changeStatus.toggle();
        if (this.blockUserShowed) this.blockUser.toggle();
        if (this.updateDocumentsShowed) this.updateDocuments.toggle();
        if (this.activePassesShowed) this.activePasses.toggle();
        if (this.viewFavouritesShowed) this.viewFavouritesUsers.toggle();
      }
    });
  }

  private applyQueryParamsToState(params: ParamMap): void {
    const filters: IUsersFilters = hasAnyFilterKey(params, USER_FILTER_KEYS)
      ? deserializeUsersFilters(params)
      : { selectedClients: [], selectedTowns: [], selectedOperators: [], selectedStatus: [] };
    this.clientsSelected = filters.selectedClients;
    this.townsSelected = filters.selectedTowns;
    this.operatorsSelected = filters.selectedOperators;
    this.statusSelected = filters.selectedStatus;

    const pageParam = params.get('page');
    if (pageParam) {
      const n = Number(pageParam);
      if (!isNaN(n) && n > 0) this.users.pagination.actualPage = n;
    } else {
      this.users.pagination.actualPage = 1;
    }

    const sizeParam = params.get('size');
    if (sizeParam) {
      const n = Number(sizeParam);
      if (!isNaN(n) && n > 0) this.users.pagination.size = n;
    }

    this.searchValue = params.get('search') ?? '';
  }

  ngAfterViewInit(): void {
    this.getUsersListener();
    this.cdr.detectChanges();
  }

  userEdited(userEdited: IUserRegular | IUserDriver) {
    const index = this.users.value.findIndex((user: IUserRegular | IUserDriver) => user.id === userEdited.id);
    if (index !== -1) {
      this.users.value[index] = userEdited;
    } else {
      this.refreshData(); // create user also calls this
      this.newUser = undefined;
      this.userSelected = userEdited;
    }
  };

  async getUsersListener() {
    this.interval = setInterval(() => {
      this.refreshData();
    }, 600000);
  };

  async refreshData() {
    this.activatedRoute.snapshot.data['users'] = this.users;
    await this.usersResolver.filter(this.activatedRoute.snapshot, this.clientsSelected, this.townsSelected, this.operatorsSelected, this.statusSelected, this.searchValue);
  }

  goToUser(user: IUser): void {
    this.router.navigate([user.type === UserType.Driver ? '/drivers' : '/users', user.id], {
      queryParams: this.activatedRoute.snapshot.queryParams,
    });
  }

  async showUserData(user: { type?: UserType, id?: number }) {
    if (!user.id) return;
    // If the URL doesn't already point at this user, navigate first.
    // The paramMap subscription will re-enter this method once the URL matches.
    const currentId = this.activatedRoute.snapshot.paramMap.get('id');
    const expectedBasePath = user.type === UserType.Driver ? '/drivers' : '/users';
    if (currentId !== String(user.id) || this.listBasePath !== expectedBasePath) {
      this.router.navigate([expectedBasePath, user.id], {
        queryParams: this.activatedRoute.snapshot.queryParams,
      });
      return;
    }
    try {
      this.userSelected = user.type === UserType.Regular
        ? ((await this.usersService.getUser(user.type!, user.id!)) as IUserRegular)
        : ((await this.usersService.getUser(user.type!, user.id!)) as IUserDriver);
      this.changeStatusSelected = this.userSelected.status;
      this.newUser = undefined;
    } catch {
      // try again in case it may be a driver
      if (user.type === UserType.Regular) {
        this.router.navigate(['/drivers', user.id], {
          queryParams: this.activatedRoute.snapshot.queryParams,
        });
      } else {
        this.router.navigate([this.listBasePath], {
          queryParams: this.activatedRoute.snapshot.queryParams,
        });
      }
    }
  }

  goToNewFavourite(): void {
    this.appComponent.setRouteName('bookings');
    this.router.navigate(['/bookings/new'],
      {
        queryParams:
        {
          'userId': this.favouriteUserId
        },
        state: {
          fromFavourite: true
        }
      }
    );
  }

  changePage(event: any) {
    const { isNext, specificPage } = event;
    const current = this.users.pagination.actualPage;
    const newPage = specificPage ?? (isNext ? current + 1 : current - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage > 1 ? newPage : null },
      queryParamsHandling: 'merge',
    });
  }

  async onFavouriteChangePage(event: any) {
    const { isNext, specificPage } = event;
    if (!specificPage) {
      this.favourites.pagination.actualPage = isNext
        ? this.favourites.pagination.actualPage + 1
        : this.favourites.pagination.actualPage - 1;
    } else {
      this.favourites.pagination.actualPage = specificPage;
    }
    this.favourites = await this.favouritesService.getFavourites(this.favouriteUserId, this.favourites.pagination.actualPage, this.favourites.pagination.size);
  }

  public getTooltipName(name: string, i: number): string {
    return `${name}-${i}`;
  }

  public closeDetails() {
    this.newUser = undefined;
    this.userSelected = undefined;
    this.router.navigate([this.listBasePath], {
      queryParams: this.activatedRoute.snapshot.queryParams,
    });
  }

  public async addUser() {
    this.newUser = initialUserRegularState(this.language);
  };

  public async addUserDriver() {
    this.newUser = initialUserDriverState(this.language);
  };

  async showFavouriteRoutes(event: Event | null, id: number) {
    const favourites = await this.favouritesService.getFavourites(id, 1, 7);
    this.favourites = favourites;
    this.favouriteUserId = id;
    const recurrents = await this.bookingSchedulersService.getBookingSchedulersByUserId(id, 1, 7) as any;
    this.recurrents = recurrents;
    this.recurrentUserId = id;
    this.viewFavouritesUsers = new Modal(document.getElementById('viewFavouritesUsers'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.viewFavouritesShowed = false),
      onShow: () => (this.viewFavouritesShowed = true),
    });
    this.viewFavouritesUsers.toggle();
    event && event.stopPropagation();
  }

  async onFavouriteTypeChange(type: Event | FavouriteType) {
    if (type === FavouriteType.PUNTUAL) {
      const favourites = await this.favouritesService.getFavourites(this.favouriteUserId, 1, 7);
      this.favourites = favourites;
    } else {
      const recurrents = await this.bookingSchedulersService.getBookingSchedulersByUserId(this.recurrentUserId, 1, 7) as any;
      this.recurrents = recurrents;
    }
  }

  public filterUsers(event: { selectedClients: number[], selectedTowns: number[], selectedOperators: number[], selectedStatus: UserStatus[] }) {
    const filters: IUsersFilters = {
      selectedClients: event?.selectedClients ?? [],
      selectedTowns: event?.selectedTowns ?? [],
      selectedOperators: event?.selectedOperators ?? [],
      selectedStatus: event?.selectedStatus ?? [],
    };
    // Persist as defaults so the next fresh visit hydrates the URL from storage.
    this.storageService.setUsersFilters(JSON.stringify(filters));
    const serializedFilters = serializeUsersFilters(filters);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...serializedFilters, page: null },
      queryParamsHandling: 'merge',
    });
  };

  public changeUserType(type: UserType) {
    // Filters are scoped to a user type; do not preserve queryParams across the switch
    this.router.navigate([type === UserType.Driver ? '/drivers' : '/users']);
  };

  public searchUsers(data: any) {
    const value = typeof data === 'object' ? data.target.value : data;
    const currentSearch = this.activatedRoute.snapshot.queryParamMap.get('search') || null;
    if ((value || null) === currentSearch) {
      this.users.isFetchingUsers = true;
      this.users.value = [];
      this.refreshData();
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: value || null, page: null },
      queryParamsHandling: 'merge',
    });
  };

  public sortUsers() {
    // The search bar already wrote the new sort value to storage; reflect it in the URL too.
    // Drop the param when it matches the default — missing key = default.
    this.storageService.getUserSortType().then((sort) => {
      const isDefault = !sort || sort === UserSortType.CREATION;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { sort: isDefault ? null : sort },
        queryParamsHandling: 'merge',
      });
    });
  };

  async handleChangePaginationSize(newSize: any) {
    const n = Number(newSize);
    const size = (!n || n <= 0) ? APP.DEFAULT_PAGE_SIZE : n;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { size: size === APP.DEFAULT_PAGE_SIZE ? null : size, page: null },
      queryParamsHandling: 'merge',
    });
  }

  abortFetchEventSource() {
    clearInterval(this.interval);
  };

  async validatePickUpAction(bookingId: any) {
    const bookingIndex = this.bookings.findIndex(
      (booking: IBooking) => bookingId === booking.id,
    )!;
    const booking = await this.tripsService.validatePickUp(bookingId);
    this.bookings[bookingIndex] = booking;
    this.utilsService.showNotification(images.notifications.check, 'trips.actions.validate', NotificationTypes.SUCCESS);
  };

  async validateDropOffAction(bookingId: any) {
    const bookingIndex = this.bookings.findIndex(
      (booking: IBooking) => bookingId === booking.id,
    )!;
    const booking = await this.tripsService.validateDropOff(bookingId);
    this.bookings[bookingIndex] = booking;
    this.utilsService.showNotification(images.notifications.check, 'trips.actions.validate', NotificationTypes.SUCCESS);
  };

  async showViewBookings(userId?: number) {
    if (!userId) return;
    this.viewBookingsUserId = userId;
    this.bookings = await this.usersService.getBookingsByUser(userId!);
    this.getBookingsInterval = setInterval(async () => {
      this.bookings = await this.usersService.getBookingsByUser(userId!);
    }, 60000);
    this.viewBookings = new Modal(document.getElementById('viewBookings'), {
      placement: 'center',
      closable: false,
      onHide: () => { clearInterval(this.getBookingsInterval); this.viewBookingsShowed = false; },
      onShow: () => (this.viewBookingsShowed = true),
    });
    this.viewBookings.toggle();
  };

  async showActivePassesModal(userId: number) {
    if (!userId || !this.userSelected) return;
    this.activePassesUserId = userId;
    // Cache the logged user's accessible clients (/me/clients) so we don't refetch
    // every open. /me/clients returns the full set the logged user can manage,
    // including all active clients for SUPERADMIN — while /me by itself returns
    // only directly-associated clients (often empty for SUPERADMIN).
    if (!this.loggedUserClientIds.length) {
      const clients = await this.usersService.getClients();
      this.loggedUserClientIds = (clients || []).map((c: any) => c.id);
    }
    const userClientIds = (this.userSelected.clients || []).map((c: any) => c.id);
    const loggedSet = new Set(this.loggedUserClientIds);
    this.activePassesAllowedClientIds = userClientIds.filter((id: number) => loggedSet.has(id));
    this.activePasses = new Modal(document.getElementById('activePasses'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.activePassesShowed = false),
      onShow: () => (this.activePassesShowed = true),
    });
    this.activePasses.toggle();
  };

  async showDocumentsModal() {
    if (this.userSelected.documents) {
      this.documents = this.userSelected.documents.reduce((groups: any, document: IDocument) => {
        const { documentType } = document;
        if (!groups[documentType]) {
          groups[documentType] = [];
        }
        groups[documentType].push(document);
        return groups;
      }, {} as { [key in IDocumentType]: IDocument[] });
      this.updateDocuments = new Modal(document.getElementById('updateDocuments'), {
        placement: 'center',
        closable: false,
        onHide: () => { this.updateDocumentsShowed = false; this.reason = ''; },
        onShow: () => (this.updateDocumentsShowed = true),
      });
      this.updateDocuments.toggle();
    }
  };

  async onUploadDocuments(documentCreateRequests: IDocumentCreate[]) {
    try {
      await this.usersService.createDocuments(this.userSelected.id, documentCreateRequests);
      this.utilsService.showNotification(images.notifications.check, 'users.documents.actions.upload.success', NotificationTypes.SUCCESS);
      this.userSelected = (await this.usersService.getUser(
        this.userSelected.type!,
        this.userSelected.id!,
      )) as IUserRegular;
      this.documents = this.userSelected.documents.reduce((groups: any, document: IDocument) => {
        const { documentType } = document;
        if (!groups[documentType]) {
          groups[documentType] = [];
        }
        groups[documentType].push(document);
        return groups;
      }, {} as { [key in IDocumentType]: IDocument[] });
    } catch (error) {
      this.utilsService.showNotification(images.notifications.warning, 'users.documents.actions.upload.error', NotificationTypes.DANGER);
    }
  };

  async deleteDocument(document: IDocument) {
    try {
      await this.usersService.deleteDocument(this.userSelected.id, document.id);
      this.userSelected = (await this.usersService.getUser(
        this.userSelected.type!,
        this.userSelected.id!,
      )) as IUserRegular;
      this.documents = this.userSelected.documents.reduce((groups: any, document: IDocument) => {
        const { documentType } = document;
        if (!groups[documentType]) {
          groups[documentType] = [];
        }
        groups[documentType].push(document);
        return groups;
      }, {} as { [key in IDocumentType]: IDocument[] });
      this.utilsService.showNotification(images.notifications.check, 'users.documents.actions.delete.success', NotificationTypes.SUCCESS);
    } catch (error) {
      this.utilsService.showNotification(images.notifications.warning, 'users.documents.actions.delete.error', NotificationTypes.DANGER);
    }
  };
  
  async downloadDocument(document: IDocument) {
    try {
      await this.usersService.getDocumentDownload(this.userSelected.id, document.id);
      this.utilsService.showNotification(images.notifications.check, 'users.documents.actions.download.success', NotificationTypes.SUCCESS);
    } catch (error) {
      this.utilsService.showNotification(images.notifications.warning, 'users.documents.actions.download.error', NotificationTypes.DANGER);
    }
  };

  async changeStatusAction() {
    this.userSelected = await this.usersService.changeStatus(this.userSelected, this.changeStatusSelected);
    this.userEdited(this.userSelected);
    const isActive = this.userSelected.status.toUpperCase() === this.userStatus.ACTIVE;
    this.changeStatus.toggle();
    this.notificationService.image = this.userSelected.status.toUpperCase() === this.userStatus.DELETED ? images.notification.userDeleted : (isActive ? images.notification.userActive : images.notification.userInactive);
    this.notificationService.title = this.literalService.get(
      `users.actions.${ this.userSelected.status }.successTitle`,
      true,
    );
    this.notificationService.message = this.literalService.get(
      `users.actions.${ this.userSelected.status.toUpperCase() === this.userStatus.DELETED ? 'Deleted' : 'edit' }.successText`,
      true,
    );
    this.notificationService.show(NotificationTypes.SUCCESS);
  };

  async blockUserAction(data: any) {
    this.userSelected = await this.usersService.changeStatus(this.userSelected, this.utilsService.capitalize(UserStatus.BLOCKED.toLowerCase()), data.date, data.reason);
    this.userEdited(this.userSelected);
    this.blockUser.toggle();
    this.notificationService.image = images.notification.userBlocked;
    this.notificationService.title = this.literalService.get(
      `users.actions.Blocked.successTitle`,
      true,
    );
    this.notificationService.message = this.literalService.get(
      `users.actions.edit.successText`,
      true,
    );
    this.notificationService.show(NotificationTypes.SUCCESS);
  }

  async showChangeStatus(status: UserStatus) {
    this.changeStatusSelected = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    if (status === UserStatus.BLOCKED) {
      this.blockUser = new Modal(document.getElementById('blockUser'), {
        placement: 'center',
        closable: false,
        onHide: () => { this.blockUserShowed = false; this.reason = ''; },
        onShow: () => { this.blockUserShowed = true; this.reason = ''; },
      });
      this.blockUser.toggle();
    } else {
      this.changeStatus = new Modal(document.getElementById('changeStatus'), {
        placement: 'center',
        closable: false,
        onHide: () => (this.changeStatusShowed = false),
        onShow: () => (this.changeStatusShowed = true),
      });
      this.changeStatus.toggle();
    }
  };

  async bookWithFavourite(favourite: IFavourites) {
    this.viewFavouritesUsers.toggle();
    this.appComponent.setRouteName('bookings');
    const recurrentBookings = await this.bookingSchedulersService.getBookingSchedulersByUserId(this.favouriteUserId, 1, 7) as any;
    this.router.navigate(
      ['/bookings/new'],
      {
        queryParams:
        {
          'favouriteId': favourite.id,
          'userId': this.favouriteUserId
        },
        state: {
          isAlreadyRecurring: recurrentBookings.value.some((recurrent: IBookingScheduler) => recurrent.favorite.id === favourite.id)
        }
      }
    );
  }

  async editFavourite(favourite: IFavourites) {
    this.viewFavouritesUsers.toggle();
    this.appComponent.setRouteName('bookings');
    this.router.navigate(['/bookings/new'],
      {
        queryParams:
        {
          'userId': this.favouriteUserId,
          'favouriteId': favourite.id,
          'editFavourite': true
        },
        state: {
          fromFavourite: true
        }
      }
    );
  }

  async deleteFavourite(favouriteId: number) {
    this.favouritesService.deleteFavourite(favouriteId).then(async () => {
      if (this.favourites.value.length === 1) this.favourites.pagination.actualPage -= 1;
      this.favourites = await this.favouritesService.getFavourites(this.favouriteUserId, this.favourites.pagination.actualPage, this.favourites.pagination.size);
      this.utilsService.showNotification(images.notifications.check, 'bookings.actions.viewFavourite.delete', NotificationTypes.SUCCESS, 'successTitle', 'successText');
    }, () => {
      this.utilsService.showNotification(images.notifications.warning, 'bookings.actions.viewFavourite.delete', NotificationTypes.DANGER, 'errorTitle', 'errorText');
    });
  }

  async editRecurrent(recurrent: IBookingScheduler) {
    this.viewFavouritesUsers.toggle();
    this.appComponent.setRouteName('bookings');
    this.router.navigate(
      ['/bookings/new'],
      {
        queryParams:
        {
          'recurrentId': recurrent.id,
          'userId': this.favouriteUserId
        }
      }
    );
  }

  async deleteRecurrent(recurrentId: number) {
    this.bookingSchedulersService.deleteBookingScheduler(recurrentId).then(async () => {
      if (this.recurrents.value.length === 1) this.recurrents.pagination.actualPage -= 1;
      const recurrents = await this.bookingSchedulersService.getBookingSchedulersByUserId(this.favouriteUserId, this.recurrents.pagination.actualPage, this.recurrents.pagination.size) as any;
      this.recurrents = recurrents;
      this.utilsService.showNotification(images.notifications.check, 'bookings.actions.viewRecurrent.delete', NotificationTypes.SUCCESS, 'successTitle', 'successText');
    }, () => {
      this.utilsService.showNotification(images.notifications.warning, 'bookings.actions.viewRecurrent.delete', NotificationTypes.DANGER, 'errorTitle', 'errorText');
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
    this.routerSubscription?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    this.routeParamsSub?.unsubscribe();
  }
}
